import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Search, BookOpen, Pill, HeartPulse } from "lucide-react";

export default function Knowledge() {
  const [data, setData] = useState({ items: [], procedures: [], disclaimer: "" });
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/knowledge").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const grouped = useMemo(() => {
    const f = data.items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()) || i.category.toLowerCase().includes(q.toLowerCase()));
    const g = {};
    f.forEach((i) => { (g[i.category] = g[i.category] || []).push(i); });
    return g;
  }, [data.items, q]);

  return (
    <div data-testid="knowledge-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Clinical Content Base</h1>
      <p className="text-slate-500 mt-1">Referenced modules covering the highest-risk medical scenarios in dentistry.</p>

      <div className="relative mt-5 max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="knowledge-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conditions, drugs, categories" className="pl-9" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {Object.entries(grouped).map(([cat, arr]) => (
          <div key={cat} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
                <HeartPulse className="w-4 h-4 text-sky-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-900">{cat}</h3>
            </div>
            <ul className="space-y-1.5">
              {arr.map((i) => (
                <li key={i.id} className="flex items-center gap-2 text-sm text-slate-700">
                  {i.kind === "medication" ? <Pill className="w-3.5 h-3.5 text-slate-400" /> : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
                  {i.name}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mt-6">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5 text-sky-600" />
          <h3 className="font-heading font-semibold text-slate-900">Procedure bleeding-risk classification (SDCEP 2022)</h3>
        </div>
        <Accordion type="single" collapsible>
          {["none", "low", "high"].map((level) => {
            const procs = data.procedures.filter((p) => p.bleeding === level);
            if (procs.length === 0) return null;
            const label = level === "none" ? "No bleeding risk" : level === "low" ? "Low bleeding risk" : "Higher bleeding risk";
            return (
              <AccordionItem key={level} value={level}>
                <AccordionTrigger data-testid={`proc-group-${level}`} className="text-sm">{label} ({procs.length})</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-1.5">
                    {procs.map((p) => <li key={p.id} className="text-sm text-slate-700">• {p.name}</li>)}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      <p className="text-xs text-slate-400 mt-4 italic">{data.disclaimer}</p>
    </div>
  );
}
